/**
 * PNG export for the compass chart.
 *
 * The chart SVG is serialised standalone, rasterised through an `Image` and
 * drawn onto a canvas together with an optional legend listing every entry in
 * chronological order. All geometry-only helpers here are pure so they can be
 * unit tested without a real canvas.
 */

import { formatCoords, sortChronologically } from './compass'
import { PALETTE, quadrantOf } from './palette'
import type { Entry } from './types'

/** Options for {@link renderCompassToPng}. */
export interface RenderOptions {
  /**
   * Device pixel scale, default 2. It is raised, never lowered, so the chart
   * area reaches {@link MIN_EXPORT_PIXELS} output pixels.
   */
  scale?: number
  /** Draw the legend under the chart, default true. */
  legend?: boolean
  /** Entries listed in the legend, in chronological order. */
  entries?: Entry[]
}

/** Title drawn at the top of the legend. */
export const LEGEND_TITLE = 'Political Compass Trajectory'

/**
 * Soft cap (in characters) for a legend description line.
 *
 * A description is bounded to `MAX_DESCRIPTION_LENGTH` (200) characters by the
 * row input, the store and the share decoder, so at this wrap width a legend row
 * carries only a handful of description lines. That bound is why the legend can
 * draw every line of every description in full — no truncation and no ellipsis —
 * without the canvas height being able to grow without bound from this change.
 *
 * The width is the character budget that survives even a line made only of the
 * widest glyphs. Description text starts at `(LEGEND_PADDING + swatch + gap) *
 * legendScale`, which is 76px at the default `legendScale` of 2, and the usable
 * width is about 1332px: the 1440px export width less that 76px left offset less
 * `LEGEND_PADDING * legendScale` (32px) of right padding. The widest Latin
 * advance is roughly 0.95em — about 22.8px at the 24px description size — so 58
 * characters of `W` would only just fit; 56 leaves headroom. It is deliberately
 * a character count and not a `measureText` result: a real font metric depends
 * on the browser and the loaded font, which would make the wrap untestable and
 * let the chart component and the export disagree about where a line breaks.
 */
export const LEGEND_WRAP_CHARS = 56

/**
 * Hard cap on the number of entry rows drawn in the legend.
 *
 * A legend row is roughly `LEGEND_LABEL_LINE + LEGEND_ROW_GAP` unscaled CSS
 * pixels (plus description lines), so an unbounded legend grows with the entry
 * count and can push the canvas past the browser's maximum canvas height — at
 * which point the export silently produces a blank or truncated image. Beyond
 * this many rows the tail is replaced by a single note saying how many entries
 * were left out.
 */
export const MAX_LEGEND_ROWS = 40

/** Legend metrics in unscaled CSS pixels; every one is multiplied by `scale`. */
export const LEGEND_PADDING = 16
export const LEGEND_TITLE_SIZE = 16
export const LEGEND_ROW_LABEL_SIZE = 13.5
export const LEGEND_ROW_DESCRIPTION_SIZE = 12
export const LEGEND_LABEL_LINE = 20
export const LEGEND_DESCRIPTION_LINE = 15
export const LEGEND_ROW_GAP = 8

const LEGEND_SWATCH_SIZE = 12
const LEGEND_SWATCH_GAP = 10
/** Muted ink for description lines and the "and N more entries" note row. */
const LEGEND_MUTED_TEXT = 'rgba(15, 23, 42, 0.72)'
const DEFAULT_SCALE = 2
/**
 * Nominal chart side length, in SVG user units. Doubles as the reference the
 * legend metrics are derived from, so the legend is identical in every export.
 */
const DEFAULT_SIZE = 720

/**
 * Smallest width, in OUTPUT pixels, of the rasterised chart area.
 *
 * The on-screen chart sizes its viewBox to the layout, so a phone can hand the
 * export a 480px SVG. Rasterised at the default device scale that would be a
 * 960px PNG — a quality regression against the 1440px a 720px chart produces —
 * so the scale is raised until the chart area reaches this floor. It is a floor
 * only: an explicit larger `scale` is never shrunk.
 */
export const MIN_EXPORT_PIXELS = 1440
const FONT_FAMILY = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
const LABEL_FONT_WEIGHT = 700

/** One legend entry: a colour swatch, a bold label and optional description lines. */
export interface LegendRow {
  id: string
  year: number
  /** Quadrant colour of the point, used for the swatch. */
  color: string
  /** Bold first line, e.g. `2016  ·  x -3.5, y -2.1`. */
  label: string
  /** Every line of the description, wrapped to {@link LEGEND_WRAP_CHARS}. */
  descriptionLines: string[]
}

/**
 * Wrap `text` on word boundaries into lines of at most `maxChars` characters.
 *
 * Words longer than a line are hard-broken. Every line of the text is returned,
 * so the caller gets the whole description: nothing is trimmed and no line is
 * ellipsized. The line count is bounded by `text` and `maxChars` alone, and only
 * loosely: at {@link LEGEND_WRAP_CHARS} a description at `MAX_DESCRIPTION_LENGTH`
 * (200) characters needs at most 7 lines — the worst case is alternating one- and
 * 55-character words, each such pair costing 57 characters for two lines.
 * Ordinary text does far better (four 49-character words wrap to exactly 4
 * lines), and `legendHeight` is handed the real count either way.
 */
export function wrapText(text: string, maxChars: number = LEGEND_WRAP_CHARS): string[] {
  const limit = Math.max(1, Math.floor(maxChars))
  const words = text.trim().split(/\s+/).filter((word) => word.length > 0)
  if (words.length === 0) return []

  const lines: string[] = []
  let current = ''
  for (const word of words) {
    let rest = word
    while (rest.length > limit) {
      if (current.length > 0) {
        lines.push(current)
        current = ''
      }
      lines.push(rest.slice(0, limit))
      rest = rest.slice(limit)
    }
    if (current.length === 0) {
      current = rest
    } else if (current.length + 1 + rest.length <= limit) {
      current = `${current} ${rest}`
    } else {
      lines.push(current)
      current = rest
    }
  }
  if (current.length > 0) lines.push(current)

  return lines
}

/**
 * Build the legend rows for `entries`, in chronological order: the swatch takes
 * the quadrant colour of the point and the label carries year and coordinates.
 */
export function buildLegendRows(entries: Entry[] | undefined): LegendRow[] {
  if (!entries || entries.length === 0) return []
  return sortChronologically(entries).map((entry) => ({
    id: entry.id,
    year: entry.year,
    color: PALETTE[quadrantOf(entry.x, entry.y)],
    label: `${entry.year}  ·  ${formatCoords(entry.x, entry.y)}`,
    descriptionLines: wrapText(entry.description ?? ''),
  }))
}

/**
 * Height (in device pixels) of the legend drawn under the chart.
 *
 * Grows with the number of rows and with the number of wrapped description
 * lines each row carries; every metric scales with `scale` so the legend is
 * pixel-sharp at any device scale. Zero rows means no legend at all. A row with
 * no entry in `descriptionLineCounts` (for example the trailing "and N more
 * entries" note) counts as a plain label line, which is exactly what
 * {@link drawLegend} gives it. Descriptions are never truncated, so a row's line
 * count is exactly what `wrapText` returned — up to a handful of lines for a
 * bounded description (at most seven at the wrap width; see
 * {@link LEGEND_WRAP_CHARS}) — and every line is charged
 * `LEGEND_DESCRIPTION_LINE` pixels, which is the same advance {@link drawLegend}
 * takes.
 */
export function legendHeight(
  rowCount: number,
  scale: number = DEFAULT_SCALE,
  descriptionLineCounts: number[] = [],
): number {
  if (rowCount <= 0) return 0

  let rowsHeight = 0
  for (let index = 0; index < rowCount; index += 1) {
    const lineCount = Math.max(descriptionLineCounts[index] ?? 0, 0)
    rowsHeight += LEGEND_LABEL_LINE + lineCount * LEGEND_DESCRIPTION_LINE + LEGEND_ROW_GAP
  }

  const unscaled =
    LEGEND_PADDING + LEGEND_TITLE_SIZE * 1.4 + LEGEND_PADDING * 0.5 + rowsHeight + LEGEND_PADDING
  return Math.round(unscaled * scale)
}

/**
 * File name for the exported PNG.
 *
 * Without a `span` it is the local date, `political-compass-YYYY-MM-DD.png`,
 * which is all a caller that has no entries can say. A caller that knows the
 * trajectory should pass its chronological **span** instead: a downloads folder
 * cannot tell two histories apart by the day they were exported, and the years
 * are what identifies a trajectory. Two entries from 2018 and 2026 come out as
 * `political-compass-2018-2026.png`, and a single year — one entry, or several
 * that share a year — names that year once, `political-compass-2018.png`.
 *
 * `from`/`to` are ordered here rather than trusted to arrive ordered, so a
 * caller that hands them over the other way round still gets a readable name.
 */
export function exportFilename(
  date: Date = new Date(),
  span?: { from: number; to: number },
): string {
  if (span) {
    const first = String(Math.min(span.from, span.to)).padStart(4, '0')
    const last = String(Math.max(span.from, span.to)).padStart(4, '0')
    return first === last
      ? `political-compass-${first}.png`
      : `political-compass-${first}-${last}.png`
  }

  const year = String(date.getFullYear()).padStart(4, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `political-compass-${year}-${month}-${day}.png`
}

/**
 * Rasterise the chart SVG into a PNG.
 *
 * The canvas is `size * scale` square, plus the legend height when a legend is
 * requested. `scale` keeps its device-pixel meaning, but it is raised as needed
 * so the chart area is never narrower than {@link MIN_EXPORT_PIXELS}. The legend
 * lists at most {@link MAX_LEGEND_ROWS} entries; when there are more, a single
 * note row says how many were left out. Resolves with the PNG blob; rejects when
 * the browser cannot give us a 2D context, cannot load the serialised SVG, or
 * produces no blob.
 */
export async function renderCompassToPng(
  svg: SVGSVGElement,
  options: RenderOptions = {},
): Promise<Blob> {
  const chartSize = readSvgSize(svg)
  const scale = Math.max(options.scale ?? DEFAULT_SCALE, MIN_EXPORT_PIXELS / chartSize)
  const chartPixels = Math.round(chartSize * scale)
  // The legend is decoupled from the rasterisation scale: it must look the same
  // in every exported image, so its metrics come from the output width against
  // the reference chart size. At the default 720/scale 2 that is exactly 2, the
  // value the legend used to be drawn at.
  const legendScale = chartPixels / DEFAULT_SIZE
  const allRows = options.legend === false ? [] : buildLegendRows(options.entries)
  const rows = allRows.slice(0, MAX_LEGEND_ROWS)
  const note = legendNote(allRows.length - rows.length)
  // The note owns a row of its own, so the height has to budget for it or the
  // canvas would be too short for the last thing drawn.
  const legendPixels = legendHeight(
    rows.length + (note === null ? 0 : 1),
    legendScale,
    rows.map((row) => row.descriptionLines.length),
  )

  const canvas = document.createElement('canvas')
  canvas.width = chartPixels
  canvas.height = chartPixels + legendPixels

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Could not export the chart: a 2D canvas context is unavailable.')
  }

  const image = await loadImage(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serializeSvg(svg, chartSize))}`,
  )

  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(image, 0, 0, chartPixels, chartPixels)

  if (rows.length > 0) {
    drawLegend(context, rows, chartPixels, canvas.width, legendScale, note)
  }

  return canvasToPngBlob(canvas)
}

/**
 * How long the object URL behind a download is kept alive, in milliseconds.
 *
 * Revoking it in the next task after `click()` is the obvious cleanup and the
 * one Safari and some Firefox builds do not tolerate: they read the blob
 * asynchronously, *after* the click handler has returned, so a URL revoked in
 * between cancels the download — Safari fails it outright and Firefox reports a
 * network error. The URL is held for a minute instead, which is far longer than
 * starting a download can take and still nothing for the tab's lifetime. An
 * object URL is a handle on a blob that is already in memory, so holding it
 * costs nothing measurable, while a cancelled download is invisible to the user
 * and to us.
 */
export const DOWNLOAD_URL_REVOKE_DELAY = 60_000

/**
 * Trigger a client-side download of `blob`, and release the object URL once no
 * browser could still be reading it (see {@link DOWNLOAD_URL_REVOKE_DELAY} for
 * the race that makes the delay a minute rather than the next task).
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, DOWNLOAD_URL_REVOKE_DELAY)
}

/** Note row for entries dropped by {@link MAX_LEGEND_ROWS}, or `null` if none. */
function legendNote(droppedCount: number): string | null {
  if (droppedCount <= 0) return null
  return `and ${droppedCount} more ${droppedCount === 1 ? 'entry' : 'entries'}`
}

/** Read the chart side length from the SVG's `width`, or fall back to `viewBox`. */
function readSvgSize(svg: SVGSVGElement): number {
  const width = Number.parseFloat(svg.getAttribute('width') ?? '')
  if (Number.isFinite(width) && width > 0) return width

  const viewBox = svg.getAttribute('viewBox')
  if (viewBox) {
    const boxWidth = Number.parseFloat(viewBox.trim().split(/[\s,]+/)[2] ?? '')
    if (Number.isFinite(boxWidth) && boxWidth > 0) return boxWidth
  }
  return DEFAULT_SIZE
}

/** SVG namespace URI used by the standalone exported document. */
export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/**
 * Clone the SVG with the attributes a standalone document needs, and return the
 * markup handed to the rasteriser.
 *
 * The `xmlns` attribute is deliberately NOT set through `setAttribute`:
 * `XMLSerializer` already emits the namespace declaration for an element that is
 * in the SVG namespace, and an explicit attribute makes it emit `xmlns` a second
 * time. A duplicate attribute is not well-formed XML, so the browser rejects the
 * image and the export fails. The namespace is re-checked afterwards as a safety
 * net for engines that do not emit it.
 */
export function serializeSvg(svg: SVGSVGElement, size: number): string {
  const clone = svg.cloneNode(true) as SVGSVGElement
  // The payload is the chart, not the interaction surface: the pointer hit areas
  // only exist to catch gestures in the live app.
  clone.querySelectorAll('[data-export-ignore]').forEach((node) => node.remove())
  clone.removeAttribute('xmlns')
  clone.setAttribute('width', String(size))
  clone.setAttribute('height', String(size))
  if (!clone.getAttribute('viewBox')) {
    clone.setAttribute('viewBox', `0 0 ${size} ${size}`)
  }

  const markup = new XMLSerializer().serializeToString(clone)
  return markup.includes(`xmlns="${SVG_NAMESPACE}"`)
    ? markup
    : markup.replace(/^<svg/, `<svg xmlns="${SVG_NAMESPACE}"`)
}

/** Load `source` into an `Image`, resolving once the browser has decoded it. */
function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () =>
      reject(new Error('Could not export the chart: the SVG could not be rasterised.'))
    image.src = source
  })
}

/** Encode the canvas as a PNG blob. */
function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('Could not export the chart: the canvas produced no PNG data.'))
    }, 'image/png')
  })
}

/**
 * Draw the legend into the strip under the chart: white background, hairline top
 * border, title, then one row per entry with a quadrant-coloured swatch, and —
 * when entries were dropped — a final muted `note` row with no swatch.
 */
function drawLegend(
  context: CanvasRenderingContext2D,
  rows: LegendRow[],
  top: number,
  width: number,
  scale: number,
  note: string | null,
): void {
  const pad = LEGEND_PADDING * scale
  const titleSize = LEGEND_TITLE_SIZE * scale
  const labelSize = LEGEND_ROW_LABEL_SIZE * scale
  const descriptionSize = LEGEND_ROW_DESCRIPTION_SIZE * scale
  const swatchSize = LEGEND_SWATCH_SIZE * scale
  const hairline = Math.max(1, scale)

  context.fillStyle = '#ffffff'
  context.fillRect(0, top, width, context.canvas.height - top)

  context.strokeStyle = 'rgba(15, 23, 42, 0.2)'
  context.lineWidth = hairline
  context.beginPath()
  context.moveTo(0, top + hairline / 2)
  context.lineTo(width, top + hairline / 2)
  context.stroke()

  context.textAlign = 'left'
  context.textBaseline = 'alphabetic'
  context.fillStyle = PALETTE.axisText
  context.font = `${LABEL_FONT_WEIGHT} ${titleSize}px ${FONT_FAMILY}`
  context.fillText(LEGEND_TITLE, pad, top + pad + titleSize)

  const textX = pad + swatchSize + LEGEND_SWATCH_GAP * scale
  let rowTop = top + pad + titleSize + LEGEND_PADDING * 0.5 * scale

  for (const row of rows) {
    context.fillStyle = row.color
    context.fillRect(pad, rowTop, swatchSize, swatchSize)
    context.strokeStyle = 'rgba(15, 23, 42, 0.25)'
    context.lineWidth = hairline
    context.strokeRect(pad, rowTop, swatchSize, swatchSize)

    context.fillStyle = PALETTE.axisText
    context.font = `${LABEL_FONT_WEIGHT} ${labelSize}px ${FONT_FAMILY}`
    context.fillText(row.label, textX, rowTop + labelSize)

    if (row.descriptionLines.length > 0) {
      context.font = `${descriptionSize}px ${FONT_FAMILY}`
      context.fillStyle = LEGEND_MUTED_TEXT
      let lineY = rowTop + LEGEND_LABEL_LINE * scale
      for (const line of row.descriptionLines) {
        lineY += descriptionSize
        context.fillText(line, textX, lineY)
        lineY += (LEGEND_DESCRIPTION_LINE - LEGEND_ROW_DESCRIPTION_SIZE) * scale
      }
    }

    rowTop += (LEGEND_LABEL_LINE + row.descriptionLines.length * LEGEND_DESCRIPTION_LINE + LEGEND_ROW_GAP) * scale
  }

  if (note !== null) {
    context.font = `${labelSize}px ${FONT_FAMILY}`
    context.fillStyle = LEGEND_MUTED_TEXT
    context.fillText(note, textX, rowTop + labelSize)
  }
}
